import React from 'react';
import { Link } from 'react-router-dom';
import UnderReview from './components/UnderReview';
import { isProductionHost } from './utils/environment';

/*
 * /blog/the-research — rebuilt 2 Sep 2026 in the article format.
 *
 * NOT YET CLINICALLY REVIEWED. The byline said "reviewed by an
 * HCPC-registered physiotherapist", which asserted a review nobody had
 * done. It now says who wrote it. The reviewer line goes back when RES-21
 * supplies a name and registration number, and the page should not be
 * promoted to production before then.
 *
 * Format from Mockups/Blog_Page_Format.html: crumb, title, standfirst, byline
 * with a named reviewer, one column at a 640px measure, claims callout in the
 * body, numbered sources with a checked date, two hand-picked related posts,
 * one matched CTA.
 *
 * The footer's three credit claims link to the anchors here, so #research,
 * #eular and #hcpc are load-bearing and must not be renamed.
 *
 * Claims discipline is stricter here than anywhere else on the site:
 *   - "Listed" in the EULAR library, never "endorsed" or "approved".
 *   - Research provenance is where the measurement came from, not evidence
 *     that the product changes outcomes.
 *   - No biomarker framing. That is investor register (QA-05).
 */

/* HELD PENDING REVIEW. RES-20 (CMA and CSO on the claims) and RES-21 (a
   named clinician for the byline) are both open, so the article below is
   written but not published. Flip UNDER_REVIEW to false when both land.
   The page and its anchors stay live either way: the footer credit lines
   point here and a dead link is worse than an honest holding page. */
/*
 * Gated by environment, not by a hand-flipped flag.
 *
 * On qa.physioactivity.com the article renders in full so it can be read and
 * reviewed in place. On the live domain it shows the holding page instead,
 * because the claims are not signed off (RES-20) and no named clinician is on
 * the byline yet (RES-21).
 *
 * Nothing needs editing to publish: when the review lands, the gate comes out
 * and the same content goes live on both.
 */
const UNDER_REVIEW = isProductionHost();

const ResearchBehindPage = () => (
    UNDER_REVIEW ? <UnderReview title="The research behind PhysioActivity" category="Evidence" /> : (
    <div className="pa2">
        <div className="art">

            <div className="artwrap">
                <div className="crumb">
                    <Link to="/blog">Blog</Link> <span>&rsaquo;</span> Evidence
                </div>

                <h1>The measurement started in a university lab</h1>

                <p className="stand">
                    Three claims sit at the bottom of every page on this site. Here is where each
                    one comes from, and, just as importantly, the limits of what it supports.
                </p>

                <div className="byline">
                    <div className="who">
                        <div className="av">PA</div>
                        <div>
                            <div className="nm">PhysioActivity</div>
                            <div className="rl">Written by the PhysioActivity team</div>
                        </div>
                    </div>
                    <div className="bmeta">
                        <span>2 September 2026</span>
                        <i />
                        <span>5 min read</span>
                    </div>
                </div>
            </div>

            <div className="artwrap artbody">
                <p>
                    Every vendor in workplace health claims a research pedigree, and most of the
                    time the claim is doing more work than the research behind it. So rather than
                    asserting ours and moving on, here is each of the three claims we make, what it
                    rests on, and what it does not entitle us to say.
                </p>

                <h2 id="research">Built on academic research</h2>
                <p>
                    The measurement behind PhysioActivity began as academic research at the
                    University of Southampton into reading physical-activity load from a
                    smartphone, rather than from a questionnaire or a step count. It was validated
                    against laboratory measurement.
                </p>
                <p>
                    That is a statement about provenance. It says the method has an academic origin
                    and was developed under scrutiny. It does not say that using it improves
                    anyone&rsquo;s outcomes, and we do not extend it that far.
                </p>

                <blockquote>
                    <p>Where a measurement came from is not the same as what it achieves.</p>
                </blockquote>

                <div className="callout">
                    <b>What this does not claim.</b> Research provenance is not outcome evidence.
                    We do not claim the service reduces absence, prevents injury, or improves
                    clinical outcomes in a workforce.
                </div>

                <h2 id="eular">Listed in the EULAR Outcome Measures Library</h2>
                <p>
                    The <a href="https://www.eular.org/" target="_blank" rel="noopener noreferrer">European
                    Alliance of Associations for Rheumatology</a> maintains a library of outcome
                    measures used in rheumatic and musculoskeletal disease. The measure is listed
                    there for osteoarthritis and rheumatoid arthritis.
                </p>
                <p>
                    <b>Listed, not endorsed.</b> A listing records that a measure exists and is
                    documented. It is not an approval, a recommendation, or a clinical
                    certification, and we do not describe it as one. If you are assessing vendors
                    formally, that distinction is worth checking in everyone&rsquo;s claims, not
                    only ours.
                </p>

                <h2 id="hcpc">HCPC-registered professionals</h2>
                <p>
                    Every programme is built and owned by a physiotherapist registered with the{' '}
                    <a href="https://www.hcpc-uk.org/" target="_blank" rel="noopener noreferrer">Health
                    and Care Professions Council</a>, the UK statutory regulator. Registration is
                    verified before access is granted, and it is not self-declared.
                </p>
                <p>
                    Being regulated means there is a route for complaint that does not run through
                    us. That is the point of saying it.
                </p>

                <h2>What we have not shown</h2>
                <p>
                    We have no deployment evidence of our own yet. Physiotherapy and exercise
                    therapy are well evidenced for MSK Health conditions, and earlier access is
                    better supported than later access. But that literature studied people already
                    in pain, usually seen in person. Our service measures everyone, before that
                    point.
                </p>
                <p>
                    The gap is the population, not the mechanism, and it is not closed. So we do
                    not attach those effect sizes to this product. Where a claim is not evidenced,
                    it is not made.
                </p>
            </div>

            <div className="artwrap">
                <h2 className="srch">Sources</h2>
                <ol className="src">
                    <li>University of Southampton. Academic origin of the load measurement method.</li>
                    <li><a href="https://www.eular.org/" target="_blank" rel="noopener noreferrer">EULAR Outcome Measures Library</a>. Listed for osteoarthritis and rheumatoid arthritis.</li>
                    <li><a href="https://www.hcpc-uk.org/" target="_blank" rel="noopener noreferrer">Health and Care Professions Council</a>. The UK statutory regulator for physiotherapists.</li>
                </ol>
                <p className="revnote">
                    Checked 2 September 2026. Reviewed annually, or whenever a claim on this page
                    changes.
                </p>

                <h2 className="srch">Read next</h2>
                <div className="rel">
                    <Link className="rc" to="/blog/who-writes-your-programme">
                        <span className="cat">How it works</span>
                        <span className="t">Who writes your programme</span>
                        <span className="x">A physiotherapist builds it. Software keeps it current.</span>
                    </Link>
                    <Link className="rc" to="/trust-centre">
                        <span className="cat">Reference</span>
                        <span className="t">Trust centre</span>
                        <span className="x">What an employer sees, and what they never will.</span>
                    </Link>
                </div>
            </div>

            <section className="artclose">
                <div className="artwrap">
                    <h2>Ask us about the evidence.</h2>
                    <p>
                        If you are assessing us formally and want to press on any of this, that is
                        the conversation we would rather have.
                    </p>
                    <Link className="btn btn-t" to="/contact">Contact us</Link>
                </div>
            </section>

        </div>
    </div>
)
);

export default ResearchBehindPage;
